import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';

interface FacilityReservation {
  id: string;
  facilityName: string;
  name: string;
  reservationDate: Date;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
}

interface RecentFacilityReservationsProps {
  reservations: FacilityReservation[];
}

const RecentFacilityReservations: React.FC<RecentFacilityReservationsProps> = ({ reservations }) => {
  const navigate = useNavigate();
  const { hasPermission, userLevel } = useAdminAuth();

  const handleReservationClick = (reservationId: string) => {
    // 최고관리자이거나 facilityReservations 권한이 있는 경우
    if (userLevel === 1 || hasPermission('facilityReservations')) {
      navigate(`/admin/facility-reservations/${reservationId}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      case 'cancelled':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">최근 시설물 예약</h2>
      <div className="space-y-4">
        {reservations.length === 0 ? (
          <p className="text-gray-500 text-sm">최근 예약 내역이 없습니다.</p>
        ) : (
          reservations.map((reservation) => (
            <div
              key={reservation.id}
              className={`border-b border-gray-200 pb-4 last:border-0 p-2 rounded-lg transition-colors ${
                (userLevel === 1 || hasPermission('facilityReservations')) ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
              onClick={() => handleReservationClick(reservation.id)}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 pr-1">
                  <p className="font-medium text-gray-900 truncate">{reservation.facilityName}</p>
                  <p className="text-sm text-gray-500 truncate">{reservation.name}</p>
                  <p className="text-sm text-gray-500">
                    {format(reservation.reservationDate, 'yyyy년 M월 d일')}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      reservation.status
                    )}`}
                  >
                    {reservation.status === 'pending' && '대기중'}
                    {reservation.status === 'approved' && '승인됨'}
                    {reservation.status === 'rejected' && '거절됨'}
                    {reservation.status === 'cancelled' && '취소됨'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentFacilityReservations; 
